/**
 * MyProjectsPanel — the projects I'm a member of, sorted by most-recently
 * modified. Each row click-throughs to the project detail page. Self-scoped via
 * `getMyListView()`.
 */

import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Badge,
  Button,
  Card,
  Group,
  Progress,
  Skeleton,
  Stack,
  Text,
  ThemeIcon,
  Title,
} from '@/components/ui';
import { IconFolders, IconArrowRight } from '@tabler/icons-react';
import { useProjectService, type ProjectModelView } from '@actschurch/ccms-sdk';
import { PROJECTS_ROUTES } from '@/config/routes';
import {
  computeHealthStatus,
  computeProgressPercentage,
  getOwnerDisplayName,
  sortProjectsByModifiedDesc,
} from '../../utils/projectHelpers';
import { HEALTH_STATUS_COLORS } from '../../utils/projectConstants';
import { MEMBER_DASHBOARD_KEY } from './dashboardConstants';

export function MyProjectsPanel() {
  const navigate = useNavigate();
  const projectService = useProjectService();

  const { data: myProjects = [], isLoading, error } = useQuery({
    queryKey: [...MEMBER_DASHBOARD_KEY, 'my-projects'],
    queryFn: () => projectService.getMyListView(),
  });

  const recent = useMemo(
    () =>
      sortProjectsByModifiedDesc(
        (myProjects as ProjectModelView[]).filter((p) => !p.isArchived)
      ).slice(0, 6),
    [myProjects]
  );

  return (
    <Card withBorder padding="lg" radius="md">
      <Group justify="space-between" mb="sm">
        <Group gap="xs">
          <ThemeIcon size="md" variant="light" color="indigo" radius="xl">
            <IconFolders size={16} />
          </ThemeIcon>
          <Title order={5}>My Projects</Title>
        </Group>
        <Button
          variant="subtle"
          size="xs"
          rightSection={<IconArrowRight size={14} />}
          onClick={() => navigate(PROJECTS_ROUTES.MY_PROJECTS)}
        >
          View all
        </Button>
      </Group>

      {isLoading ? (
        <Stack gap="xs">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} height={48} radius="sm" />
          ))}
        </Stack>
      ) : error ? (
        <Text size="sm" c="red" ta="center" py="md">
          Could not load your projects: {error instanceof Error ? error.message : 'service unavailable'}
        </Text>
      ) : recent.length === 0 ? (
        <Text size="sm" c="dimmed" ta="center" py="md">
          You are not on any project yet.
        </Text>
      ) : (
        <Stack gap="xs">
          {recent.map((p: ProjectModelView) => {
            const progress = computeProgressPercentage(p);
            const health = computeHealthStatus(p);
            const healthColor = HEALTH_STATUS_COLORS[health] ?? 'blue';
            return (
              <Card
                key={p.projectID}
                withBorder
                padding="sm"
                radius="sm"
                style={{ cursor: 'pointer' }}
                onClick={() => navigate(PROJECTS_ROUTES.VIEW(p.projectID))}
              >
                <Group justify="space-between" wrap="nowrap">
                  <Stack gap={2} style={{ minWidth: 0, flex: 1 }}>
                    <Text fw={600} size="sm" truncate>
                      {p.title || `Project ${p.projectID}`}
                    </Text>
                    <Text size="xs" c="dimmed" truncate>
                      {getOwnerDisplayName(p)}
                    </Text>
                  </Stack>
                  <Group gap="sm" wrap="nowrap" style={{ width: 200 }}>
                    <Progress value={progress} size="sm" style={{ flex: 1 }} />
                    <Text size="xs" c="dimmed" w={34} ta="right">
                      {progress}%
                    </Text>
                    <Badge color={healthColor} variant="light" size="sm">
                      {health}
                    </Badge>
                  </Group>
                </Group>
              </Card>
            );
          })}
        </Stack>
      )}
    </Card>
  );
}
